import { useState } from 'react'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { Label } from '@/common/ui/label'
import { Textarea } from '@/common/ui/textarea'
import useMutationAddCreditToPartner from '@/modules/manage-player/api/hooks/useMutationAddCreditToPartner'
import { GetPartnerDataResponse } from '@/modules/manage-player/type'
import { AlertTriangle, PlusCircle } from 'lucide-react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'

interface AddCreditDialogProps {
	partner: GetPartnerDataResponse
}

export default function AddCreditDialog({ partner }: AddCreditDialogProps) {
	const [open, setOpen] = useState(false)
	const [amount, setAmount] = useState('')
	const [note, setNote] = useState('')

	const { mutate: addCredit, isPending } = useMutationAddCreditToPartner()

	const handleSubmit = () => {
		const parsedAmount = parseFloat(amount)
		if (isNaN(parsedAmount) || parsedAmount <= 0) {
			toast.error('กรุณาระบุจำนวนเงินที่ถูกต้อง')
			return
		}

		addCredit(
			{
				partnerId: partner.id,
				payload: {
					amount: parsedAmount,
					note: note || undefined,
				},
			},
			{
				onSuccess: () => {
					toast.success(
						`เพิ่มเครดิต ${parsedAmount.toLocaleString()} บาท ให้พาร์ทเนอร์ ${partner.nickname} สำเร็จ`,
					)
					setOpen(false)
					setAmount('')
					setNote('')
				},
				onError: (error) => {
					showErrorToast(error)
				},
			},
		)
	}

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="outline" size="sm" className="flex items-center gap-1">
					<PlusCircle className="h-4 w-4" />
					เพิ่มเครดิต
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle>เพิ่มเครดิตให้พาร์ทเนอร์</DialogTitle>
					<DialogDescription>
						เพิ่มเครดิตให้พาร์ทเนอร์: {partner.nickname}
					</DialogDescription>
				</DialogHeader>
				<div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-300">
					<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
					<div className="space-y-1">
						<p>
							ใช้สำหรับการจ่ายส่วนแบ่งโดยการ
							<strong>เพิ่มเครดิตเข้าบัญชีพาร์ทเนอร์โดยตรง</strong>
							พาร์ทเนอร์สามารถถอนเครดิตดังกล่าวออกได้เองผ่านช่องทางการถอนเงินของระบบ
						</p>
						<p>
							หลังจากจ่ายส่วนแบ่งด้วยวิธีนี้ อย่าลืมไปบันทึกประวัติที่หน้า{' '}
							<strong>ประวัติจ่ายส่วนแบ่งพาร์ทเนอร์</strong>
						</p>
					</div>
				</div>
				<div className="grid gap-4 py-4">
					<div className="grid gap-2">
						<Label htmlFor="amount">จำนวนเงิน (บาท)</Label>
						<Input
							id="amount"
							type="number"
							placeholder="0.00"
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
							min={0}
							step="0.01"
						/>
					</div>
					<div className="grid gap-2">
						<Label htmlFor="note">หมายเหตุ (ไม่บังคับ)</Label>
						<Textarea
							id="note"
							placeholder="หมายเหตุ..."
							value={note}
							onChange={(e) => setNote(e.target.value)}
						/>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={() => setOpen(false)}>
						ยกเลิก
					</Button>
					<Button onClick={handleSubmit} disabled={isPending || !amount}>
						{isPending ? 'กำลังดำเนินการ...' : 'ยืนยัน'}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
